/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable camelcase */
import { Pie } from "@ant-design/plots";
import { DatePicker, Descriptions, Select, Switch, Table } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import { useGraphTheme } from "../../../../hooks/useGraphTheme";
import { showErrorNotification } from "../../../../utils/notification";
import { clamp } from "../../../../utils/responsiveHelpers";

const DiscordAnalyticsMentorExpenseAnalysisDash = ({ ROOT_URL }) => {
  const graphTheme = useGraphTheme();
  const currentDate = new Date();
  const oneMonthAgo = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() - 1,
    currentDate.getDate()
  );
  const mentorQueriesPickedPieConfig = {
    appendPadding: 10,
    angleField: "value",
    colorField: "name",
    radius: 1,
    innerRadius: 0.6,
    label: {
      text: "value",
      style: {
        textAlign: "center",
        fontSize: 14
      }
    },
    interactions: [{ type: "element-selected" }, { type: "element-active" }],
    statistic: {
      title: "reason",
      content: {
        style: {
          whiteSpace: "pre-wrap",
          overflow: "hidden",
          textOverflow: "ellipsis"
        }
      }
    },
    annotations: [
      {
        type: "text",
        style: {
          text: "Mentor Queries Picked",
          x: "50%",
          y: "50%",
          textAlign: "center",
          fontSize: 20,
          fontStyle: "bold"
        }
      }
    ]
  };

  const [totalPayout, setTotalPayout] = useState(0);
  const [totalThanks, setTotalThanks] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [totalLevelWiseThanks, setTotalLevelWiseThanks] = useState({});
  const [leaderboardRecords, setLeaderboardRecords] = useState([]);
  const [batchesList, setBatchesList] = useState([]);
  const [useRealtimeThanks, setUseRealtimeThanks] = useState(false);
  const [filter, setFilter] = useState({
    startDate: oneMonthAgo,
    endDate: currentDate,
    batchId: null,
    useRealtimeThanks: false,
    is_on_platform: true
  });
  const [mentorQueryPickedData, setMentorQueryPickedData] = useState([]);

  const fetchMentorsLeaderboard = () => {
    axios
      .get(`${ROOT_URL}/admin/discord-analytics/mentors/performance-analytics/leaderboard-data`, {
        params: {
          filter
        }
      })
      .then((res) => {
        const {
          mentorLeaderboardData = [],
          totalPayout = 0,
          totalThanks = 0,
          totalPoints = 0,
          totalLevelWiseThanks = {}
        } = res.data;
        setLeaderboardRecords(mentorLeaderboardData);
        setTotalPayout(totalPayout);
        setTotalThanks(totalThanks);
        setTotalPoints(totalPoints);
        setTotalLevelWiseThanks(totalLevelWiseThanks);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchWebsiteBatchesList = () => {
    axios
      .get(`${ROOT_URL}/admin/discord-analytics/mentors/performance-analytics/batches-list`, {})
      .then((res) => {
        const { batchesList = [] } = res.data;
        setBatchesList(batchesList);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchMentorQueriesPickedStats = () => {
    axios
      .get(
        `${ROOT_URL}/admin/discord-analytics/mentors/performance-analytics/mentor-queries-picked-stats`,
        {
          params: {
            filter
          }
        }
      )
      .then((res) => {
        setMentorQueryPickedData(res.data);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const leaderboardTableColumns = [
    {
      title: "S No.",
      dataIndex: "index",
      key: "index"
    },
    {
      title: "Discord Id",
      dataIndex: "id",
      key: "id"
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => {
        if (typeof a.name === "string" && typeof b.name === "string") {
          return a.name?.localeCompare(b.name) | 0;
        }
        // Fallback comparison if not string (handle the case if one or both values are not strings)
        return 0;
      }
    },
    {
      title: "Level 1",
      dataIndex: "Level 1",
      key: "Level 1",
      sorter: (a, b) => a["Level 1"] - b["Level 1"]
    },
    {
      title: "Level 2",
      dataIndex: "Level 2",
      key: "Level 2",
      sorter: (a, b) => a["Level 2"] - b["Level 2"]
    },
    {
      title: "Level 3",
      dataIndex: "Level 3",
      key: "Level 3",
      sorter: (a, b) => a["Level 3"] - b["Level 3"]
    },
    {
      title: "Level 4",
      dataIndex: "Level 4",
      key: "Level 4",
      sorter: (a, b) => a["Level 4"] - b["Level 4"]
    },
    {
      title: "Total Thanks",
      dataIndex: "totalThanks",
      key: "totalThanks",
      sorter: (a, b) => a.totalThanks - b.totalThanks
    },
    {
      title: "Total Points",
      dataIndex: "points",
      key: "thankPoints",
      sorter: (a, b) => a.points - b.points
    },
    {
      title: "Total Pay (in INR)",
      dataIndex: "payout",
      key: "payout",
      sorter: (a, b) => a.payout - b.payout
    }
  ];

  useEffect(() => {
    fetchWebsiteBatchesList();
    fetchMentorsLeaderboard();
    fetchMentorQueriesPickedStats();
  }, [filter]);

  return (
    <div
      style={{
        marginTop: "20px",
        marginLeft: clamp(12, 30),
        marginRight: clamp(12, 30)
      }}
    >
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: "10px",
          alignItems: "center"
        }}
      >
        <div>
          Mentor Batch:{" "}
          <Select
            showSearch
            placeholder="Select Batch"
            style={{ width: "min(200px, 100%)" }}
            optionFilterProp="children"
            onChange={(value) => {
              setFilter((prevFilter) => ({
                ...prevFilter,
                batchId: value
              }));
            }}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {(batchesList || []).map((batch) => (
              <Select.Option value={batch.value} key={batch.value}>
                {batch.label}
              </Select.Option>
            ))}
          </Select>
        </div>
        <div>
          Date Range:{" "}
          <DatePicker.RangePicker
            value={
              filter.startDate && filter.endDate
                ? [dayjs(filter.startDate), dayjs(filter.endDate)]
                : []
            }
            onChange={(dates) => {
              if (dates) {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  startDate: dates[0].clone().startOf("day").toDate(),
                  endDate: dates[1].clone().endOf("day").toDate()
                }));
              } else {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  startDate: null,
                  endDate: null
                }));
              }
            }}
          />
        </div>
        <div>
          Use realtime thanks{" "}
          <Switch
            checked={useRealtimeThanks}
            onChange={(checked) => {
              setUseRealtimeThanks(checked);
              setFilter((prevFilter) => ({
                ...prevFilter,
                useRealtimeThanks: checked
              }));
            }}
          />
        </div>
        <div>
          Show Only Active Mentors{" "}
          <Switch
            checked={filter.is_on_platform}
            onChange={(checked) =>
              setFilter((prev) => ({
                ...prev,
                is_on_platform: checked
              }))
            }
          />
        </div>
      </div>
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "5px"
        }}
      >
        <Descriptions title="" bordered column={{ xs: 1, sm: 2, md: 3 }}>
          <Descriptions.Item label="Total Payout">{totalPayout}</Descriptions.Item>
          <Descriptions.Item label="Total Thanks">{totalThanks}</Descriptions.Item>
          <Descriptions.Item label="Total Points">{totalPoints}</Descriptions.Item>
        </Descriptions>
        <Descriptions title="Level Wise Thanks" bordered column={{ xs: 1, sm: 2, md: 3 }}>
          {Object.keys(totalLevelWiseThanks).map((level) => {
            return (
              <Descriptions.Item key={level} label={`${level}`}>
                {totalLevelWiseThanks[level]}
              </Descriptions.Item>
            );
          })}
        </Descriptions>
        <div
          style={{
            marginBottom: "10px",
            display: "flex",
            flexDirection: "column",
            gap: "5px"
          }}
        >
          <h3>Mentors Queries Picked Stats</h3>
          <Pie
            key={JSON.stringify(mentorQueryPickedData)}
            data={mentorQueryPickedData || []}
            {...mentorQueriesPickedPieConfig}
            {...graphTheme}
            legend={{
              ...mentorQueriesPickedPieConfig.legend,
              ...graphTheme.legend
            }}
          />
        </div>
        <h3>Mentor Thank Leaderboard</h3>
        <Table
          columns={leaderboardTableColumns}
          dataSource={leaderboardRecords}
          bordered
          pagination={false}
          scroll={{ x: "max-content" }}
        />
      </div>
    </div>
  );
};

export default DiscordAnalyticsMentorExpenseAnalysisDash;
